import { useState, useEffect } from 'react'
import { useAuth } from 'hooks/use-auth'
import { usePermissions } from 'hooks/use-permissions'
import { LargeCard } from 'Components/Cards/Large'
import { Role } from 'core/types'
import { useGetMyDuplicatesQuery, useGetDuplicateStatsQuery } from 'slices/duplicateChildSlice'
import { Typography, Box, Avatar, Alert, Stack } from '@mui/material'
import { TextFieldWithLabel } from 'Components/TextField'
import { type SearchCriteria, usePagination, type PaginationProps } from 'hooks/use-pagination'
import DataTable from 'Components/Table/DataTable'
import { Column, ColumnSort, ColumnType, type RowData } from 'Components/Table/DataTable/DataTable.model'
import S3Avatar from 'Components/S3Image/S3Avatar'
import { DuplicateIcon } from 'Assets/SVGs/Duplicate'
import { DuplicateStatusChip } from 'Components/DuplicateHandling/DuplicateStatusChip'

export function MyCreatedDuplicates (): JSX.Element {
  const [nameFilter, setNameFilter] = useState<string | null>(null)
  const { user } = useAuth()
  const perms = usePermissions()
  const { data: duplicateList, isLoading: isLoadingDuplicates } = useGetMyDuplicatesQuery()
  const { data: stats, isLoading: isLoadingStats } = useGetDuplicateStatsQuery()

  const paginationProps: PaginationProps<RowData> = {
    rows: duplicateList ?? [],
    initialSize: 5,
    pageSizeSteps: [5, 10]
  }
  const pagination = usePagination(paginationProps)

  useEffect(() => {
    const fieldArray: SearchCriteria[] = []
    if (nameFilter !== '' && nameFilter !== null) {
      fieldArray.push({
        field: 'childName',
        value: nameFilter
      })
    }
    pagination.setSearchFields(fieldArray)
  }, [nameFilter])

  const nameColumn = new Column('childName', 'Child Name', ColumnType.CUSTOM)
  nameColumn.customCellGenerator = row => {
    return <div style={{ whiteSpace: 'nowrap' }}>
      {row.imageKey == null
        ? <Avatar sx={{ backgroundColor: '#7136BC', display: 'inline-grid' }}>{String(row.childName)[0]}</Avatar>
        : <S3Avatar imageKey={row.imageKey} height='50px' width='50px' style={{ display: 'inline-grid' }} />}
      <span style={{ whiteSpace: 'nowrap', paddingLeft: '5px' }}>{row.childName}</span>
    </div>
  }

  const statusColumn = new Column('wasChildCreated', 'Status', ColumnType.CUSTOM)
  statusColumn.customCellGenerator = row => {
    const status = !row.isResolved ? 'Pending' : row.wasChildCreated ? 'Approved' : 'Declined'

    return <DuplicateStatusChip status={status} resolutionReason={row.resolutionReason} />
  }
  const accessGrantedColumn = new Column('accessGranted', 'Access', ColumnType.CUSTOM)
  accessGrantedColumn.customCellGenerator = row => {
    if (row.isResolved) {
      const status = row.accessGranted ? 'Approved' : 'Declined'

      return <DuplicateStatusChip status={status} resolutionReason={null} />
    } else {
      return <></>
    }
  }

  const entityColumn = new Column('isd', 'Resolving Entity')

  const columns = [nameColumn, statusColumn, accessGrantedColumn, entityColumn]

  const cardContent =
    (duplicateList == null || duplicateList.length === 0)
      ? <Typography variant='body1'>You have no duplicates at this time.</Typography>
      : <>
        <Box sx={{ paddingRight: '30px' }}>
          <Box sx={{ minWidth: '300px' }}>
            <TextFieldWithLabel
              label='Child Name'
              onChange={(e) => { setNameFilter(e.target.value) }}
              name='childName'
              maxWidth='300px'
              value={nameFilter}
              data-testid="name-search"
              placeHolder='Last or First Name'
            />
          </Box>

          <DataTable
            name='myDuplicatesTable'
            data-testid='myDuplicatesTable'
            columns={columns}
            rows={pagination.internalRows}
            page={pagination.page}
            hasCheckbox={false}
            onPageChange={pagination.handleChangePage}
            totalRecords={pagination.recordCount}
            onSortChange={(col, sorts) => {
              pagination.onSortChange(col, sorts[0].order)
            }}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
            initialColumnSorts={[new ColumnSort('childName')]}
            loading={false}
            rowClickable={false}
          />

          <Box sx={{ paddingRight: '30px' }}>
            <Alert severity="info" icon={<></>} sx={{ border: '1px solid #3694BC', borderRadius: '12px', padding: '10px 20px' }}>
              <Stack direction='row' spacing={2}>
                <Box sx={{ paddingTop: '5px' }}>
                  <DuplicateIcon fill='#7136BC' />
                </Box>
                <Stack direction='row' spacing={1}>
                  <Typography>You have submitted {stats?.submittedLast90Days} duplicate{stats?.submittedLast90Days === 1 ? '' : 's'} in the last 90 days</Typography>
                </Stack>
              </Stack>
            </Alert>
          </Box>
        </Box>
      </>

  if (isLoadingDuplicates || isLoadingStats || user == null || (Boolean(perms.ISDs_DuplicateHandling) || user.securityLevel === Role.CARE_GIVER || user.securityLevel === Role.MIECC_ADMIN)) {
    return <></>
  } else {
    return <>
      <LargeCard header='My Created Duplicates' headerClassName='info-box-header' sx={{ width: '100%' }} content={cardContent} />
    </>
  }
}
